/**
 * Друкована форма «Акт комплектації номенклатури» (довільна форма, ч.676):
 * організація, № і дата, комплект/склад оприбуткування/к-сть, таблиця
 * компонентів зі складом списання ПО РЯДКАХ (0460), собівартість для
 * проведеного; підписи. Єдиний стиль друку (канон ч.669).
 */
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { productAssemblies } from '@/api/productAssemblies';
import { orgs, nomenclature as nomApi } from '@/api/catalogs';
import { refs } from '@/api/refs';
import PrintActions from '@/shared/components/PrintActions';
import { partyCodesText } from '@/shared/utils/partyCodes';

const fmtQty = (n: number | string) =>
  Number(n).toLocaleString('uk-UA', { maximumFractionDigits: 3 });
const fmtMoney = (n: number | string | null | undefined) =>
  Number(n ?? 0).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function ProductAssemblyPrintPage() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    document.body.classList.add('print-mode');
    return () => document.body.classList.remove('print-mode');
  }, []);

  const docQ = useQuery({
    queryKey: ['product-assembly', id],
    queryFn: () => productAssemblies.get(id!),
    enabled: !!id,
  });
  const d = docQ.data;
  const orgQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const org = orgQ.data?.items.find((o) => o.id === d?.organization_id);
  const whQ = useQuery({
    queryKey: ['warehouses', d?.organization_id],
    queryFn: () => refs.warehouses(d!.organization_id),
    enabled: !!d?.organization_id,
  });
  const nomListQ = useQuery({
    queryKey: ['nomenclature-full'],
    queryFn: () => nomApi.listAll(),
  });
  const uomsQ = useQuery({ queryKey: ['uoms'], queryFn: refs.uoms });

  if (!d || !org) {
    return <div style={{ padding: '2rem' }}>Завантаження акта…</div>;
  }

  const whName = new Map((whQ.data ?? []).map((w) => [w.id, w.name]));
  const nomById = new Map((nomListQ.data?.items ?? []).map((n) => [n.id, n]));
  const uomById = new Map((uomsQ.data ?? []).map((u) => [u.id, u.short_name]));
  // Склад рядка > склад шапки (для компонентів шапка — лише дефолт
  // списання; 0460). Колонка — лише коли ефективних складів > 1.
  const effWh = (c: { warehouse_id?: string | null }) => c.warehouse_id || d.warehouse_id || '';
  const multiWarehouse = new Set(d.components.map(effWh).filter(Boolean)).size > 1;
  const uomOf = (it: { nomenclature_id: string; uom_id?: string | null }) => {
    if (it.uom_id) return uomById.get(it.uom_id) ?? 'шт';
    const n = nomById.get(it.nomenclature_id);
    return (n?.base_uom_id && uomById.get(n.base_uom_id)) || 'шт';
  };
  const kit = nomById.get(d.assembled_nomenclature_id);
  const compTotal = d.components.reduce((s, c) => s + Number(c.total_cost ?? 0), 0);

  return (
    <div className="print-page" style={{
      maxWidth: 800, margin: '0 auto', padding: '1.5rem',
      fontFamily: "'Times New Roman', serif", fontSize: '10.5pt', background: 'var(--bg-panel)',
    }}>
      <style>{`
        .pa-table { width: 100%; border-collapse: collapse; margin: 0.5rem 0; font-size: 9pt; line-height: 1.15; }
        .pa-table th, .pa-table td { border: 1px solid #000; padding: 1px 4px; }
        .pa-table th { text-align: center; font-weight: 600; font-size: 8pt; background: #f5f5f5; text-transform: none; letter-spacing: 0; }
      `}</style>

      <PrintActions docMeta={{
        organizationId: d.organization_id,
        documentType: 'doc.product_assemblies',
        documentId: d.id,
        filename: `Комплектація ${d.document_number}`,
        orgName: org.name,
      }} />

      <div style={{ textAlign: 'center', marginBottom: '0.5rem' }}>
        <div style={{ fontSize: '10pt' }}>{org.full_name || org.name}</div>
        {partyCodesText(org) && <div style={{ fontSize: '10pt', color: '#555' }}>{partyCodesText(org)}</div>}
      </div>

      <h2 style={{ textAlign: 'center', margin: '0.75rem 0' }}>
        Акт комплектації номенклатури № {d.document_number}
        {' '}від {new Date(d.document_date).toLocaleDateString('uk-UA')}
      </h2>

      <table style={{ width: '100%', fontSize: '10pt', marginBottom: '0.5rem' }}>
        <tbody>
          <tr>
            <td style={{ width: '55%' }}>
              <span style={{ color: '#555' }}>Комплект: </span>
              <strong>{kit?.name ?? '—'}</strong>
              {kit?.code && <span style={{ color: '#555' }}> ({kit.code})</span>}
            </td>
            <td>
              <span style={{ color: '#555' }}>Кількість: </span>
              {fmtQty(d.assembled_quantity)} {uomOf({ nomenclature_id: d.assembled_nomenclature_id })}
            </td>
          </tr>
          <tr>
            <td colSpan={2}>
              <span style={{ color: '#555' }}>Склад оприбуткування комплекту: </span>
              {whName.get(d.warehouse_id) ?? '—'}
            </td>
          </tr>
        </tbody>
      </table>

      <table className="pa-table">
        <thead>
          <tr>
            <th style={{ width: 30 }}>№</th>
            <th>Компонент (найменування)</th>
            {multiWarehouse && <th style={{ width: 130 }}>Склад списання</th>}
            <th style={{ width: 45 }}>Од.</th>
            <th style={{ width: 80 }}>К-сть</th>
            {d.posted && <th style={{ width: 80 }}>Ціна</th>}
            {d.posted && <th style={{ width: 90 }}>Сума</th>}
          </tr>
        </thead>
        <tbody>
          {d.components.map((c, i) => {
            const nom = nomById.get(c.nomenclature_id);
            return (
              <tr key={i}>
                <td style={{ textAlign: 'center' }}>{i + 1}</td>
                <td>{nom?.name ?? '—'}{nom?.code && <span style={{ color: '#555' }}> ({nom.code})</span>}</td>
                {multiWarehouse && <td>{whName.get(effWh(c)) ?? '—'}</td>}
                <td style={{ textAlign: 'center' }}>{uomOf(c)}</td>
                <td style={{ textAlign: 'right' }}>{fmtQty(c.quantity)}</td>
                {d.posted && <td style={{ textAlign: 'right' }}>{fmtMoney(c.cost_per_unit)}</td>}
                {d.posted && <td style={{ textAlign: 'right' }}>{fmtMoney(c.total_cost)}</td>}
              </tr>
            );
          })}
        </tbody>
        {d.posted && (
          <tfoot>
            <tr style={{ fontWeight: 700 }}>
              <td colSpan={multiWarehouse ? 5 : 4} style={{ textAlign: 'right' }}>
                Разом собівартість комплекту:
              </td>
              <td></td>
              <td style={{ textAlign: 'right' }}>{fmtMoney(d.total_cost ?? compTotal)}</td>
            </tr>
          </tfoot>
        )}
      </table>

      <p style={{ margin: '0.5rem 0' }}>
        Всього компонентів {d.components.length}
        {d.posted && <>, собівартість комплекту <strong>{fmtMoney(d.total_cost ?? compTotal)} грн</strong></>}.
      </p>
      {d.comment && <p style={{ margin: '0.5rem 0', color: '#333' }}>Коментар: {d.comment}</p>}

      <table style={{ width: '100%', marginTop: 28, fontSize: '10.5pt' }}>
        <tbody>
          <tr>
            <td style={{ width: '50%', verticalAlign: 'top' }}>
              <strong>Склав (відповідальний)</strong><br /><br />
              _______________________
              <br /><span style={{ color: '#333', fontSize: '9pt' }}>підпис, П.І.Б.</span>
            </td>
            <td style={{ width: '50%', verticalAlign: 'top' }}>
              <strong>Прийняв (МВО складу)</strong><br /><br />
              _______________________
              <br /><span style={{ color: '#333', fontSize: '9pt' }}>підпис, П.І.Б.</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
